import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader, EmptyState } from '@/components/ui-bits';
import { MessageSquareText, Plus, Save, Trash2, X, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY = {
  title: '',
  body: '',
  shortcut: '',
  channels: ['whatsapp', 'email', 'webchat'],
  enabled: true,
  order: 0,
};

export default function QuickRepliesPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/quick-replies');
      setItems(data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al cargar');
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editing?.title?.trim() || !editing?.body?.trim()) {
      toast.error('Título y cuerpo son obligatorios');
      return;
    }
    try {
      const payload = {
        ...editing,
        shortcut: editing.shortcut || null,
        channels: editing.channels?.length ? editing.channels : ['whatsapp', 'email', 'webchat'],
      };
      if (editing.id) await api.patch(`/quick-replies/${editing.id}`, payload);
      else await api.post('/quick-replies', payload);
      toast.success('Guardado');
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar');
    }
  };

  const del = async (item) => {
    if (!confirm(`¿Eliminar "${item.title}"?`)) return;
    try {
      await api.delete(`/quick-replies/${item.id}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const seed = async () => {
    try {
      const { data } = await api.post('/quick-replies/seed-defaults');
      toast.success(data.created ? `${data.created} respuestas creadas` : 'Ya hay respuestas');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const toggleChannel = (ch) => {
    const set = new Set(editing.channels || []);
    if (set.has(ch)) set.delete(ch);
    else set.add(ch);
    setEditing({ ...editing, channels: [...set] });
  };

  return (
    <div>
      <PageHeader
        title="Respuestas rápidas"
        description="Atajos para el Inbox. Variables: (nombre_cliente), (nombre_empleado), (nombre_empresa), (telefono_cliente)…"
        actions={(
          <div className="flex gap-2">
            <button type="button" className="btn-ghost" onClick={seed}>
              <Sparkles className="h-4 w-4" /> Plantillas
            </button>
            <button type="button" className="btn-primary" onClick={() => setEditing({ ...EMPTY })}>
              <Plus className="h-4 w-4" /> Nueva
            </button>
          </div>
        )}
      />

      {items.length === 0 ? (
        <EmptyState
          icon={MessageSquareText}
          title="Sin respuestas rápidas"
          description="Crea atajos o carga plantillas de ejemplo."
          cta={<button type="button" className="btn-primary" onClick={seed}>Cargar ejemplos</button>}
        />
      ) : (
        <div className="surface overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-border">
                <th className="px-3 py-2">Orden</th>
                <th className="px-3 py-2">Título</th>
                <th className="px-3 py-2">Atajo</th>
                <th className="px-3 py-2">Cuerpo</th>
                <th className="px-3 py-2">Estado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-border/60 hover:bg-accent/50">
                  <td className="px-3 py-2 tabular-nums">{r.order ?? 0}</td>
                  <td className="px-3 py-2 font-medium">{r.title}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.shortcut || '—'}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground max-w-md truncate">{r.body}</td>
                  <td className="px-3 py-2 text-xs">{r.enabled !== false ? 'Activa' : 'Off'}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button type="button" className="btn-ghost h-7 px-2 text-xs" onClick={() => setEditing(r)}>Editar</button>
                    <button type="button" className="btn-ghost h-7 w-7 p-0 text-[hsl(var(--danger))]" onClick={() => del(r)}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setEditing(null)}>
          <div className="surface w-full max-w-xl p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display font-semibold">{editing.id ? 'Editar respuesta' : 'Nueva respuesta'}</h2>
              <button type="button" className="btn-ghost h-8 w-8 p-0" onClick={() => setEditing(null)}>
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-3 text-sm">
              <label className="block">Título
                <input className="input mt-1" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">Atajo (ej. /hola)
                  <input className="input mt-1 font-mono" value={editing.shortcut || ''} onChange={(e) => setEditing({ ...editing, shortcut: e.target.value })} placeholder="/hola" />
                </label>
                <label className="block">Orden
                  <input type="number" className="input mt-1" value={editing.order ?? 0} onChange={(e) => setEditing({ ...editing, order: Number(e.target.value) })} />
                </label>
              </div>
              <label className="block">Cuerpo
                <textarea className="input mt-1 min-h-[140px]" value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} />
              </label>
              <div className="flex flex-wrap gap-3 text-xs">
                {['whatsapp', 'email', 'webchat'].map((ch) => (
                  <label key={ch} className="flex items-center gap-1.5">
                    <input type="checkbox" checked={(editing.channels || []).includes(ch)} onChange={() => toggleChannel(ch)} />
                    {ch}
                  </label>
                ))}
                <label className="flex items-center gap-1.5 ml-auto">
                  <input type="checkbox" checked={editing.enabled !== false} onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} />
                  Activa
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button type="button" className="btn-ghost" onClick={() => setEditing(null)}>Cancelar</button>
              <button type="button" className="btn-primary" onClick={save}><Save className="h-4 w-4" /> Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
